import { z } from 'zod';
import {
  CurrencyCode,
  DeliveryWindow,
  Email,
  GUEST_NOTE_MAX,
  IdempotencyKey,
  ImageUrl,
  Instant,
  Locale,
  MinorUnitsNonNegative,
  Ok,
  PageRequest,
  PhoneE164,
  PublicId,
  pageOf,
  ServiceDay,
  TimeZone,
  Uuid,
} from '../common';
import { RatingSubject, StorefrontMode, SubstitutionChoice } from '../enums';
import { defineEndpoint } from '../http';
import {
  AddressInstruction,
  ByLocaleText,
  CatalogueItem,
  DeliveryOverride,
  DocumentSummary,
  IssueReport,
  OrderDetail,
  OrderSummary,
  PriceBreakdown,
  ProductsInvoiceStanding,
  ProductType,
  ShopCard,
  ShopFacts,
} from '../models';
import { StaySkipReason } from '../projections/schedule-occurrence';

/**
 * `/g` - the guest storefront (tech-stack section 6).
 *
 * Auth is a signed, revocable QR session token. There is no identity provider and
 * no account: ordering must take under three minutes (tech-stack section 7). The
 * token is a bearer credential, which is why the access code is masked until an
 * explicit, logged reveal (PRD GST-024, NFR-009). Neither the token nor its
 * signature incorporates the host, so a printed QR keeps validating after the
 * domain migration (PRD OPS-020).
 */

// ---------------------------------------------------------------------------
// Session (PRD GST-001, GST-006, GST-028, GST-040)
// ---------------------------------------------------------------------------

export const GuestSession = z.object({
  /** Opaque session token; never exposes an enumerable property identifier. */
  sessionToken: z.string().min(16).max(2048),
  /**
   * PRD GST-034 / NFR-008 - the durable half of the guest's identity, minted on
   * the first scan and echoed on every later one. The browser keeps it and
   * replays it so this guest's own orders survive session expiry; a browser
   * that has none is a different guest and sees none of these orders. Opaque
   * and signed: it is a bearer credential, not an identifier to display.
   */
  guestKey: z.string().min(8).max(256),
  expiresAt: Instant,
  locale: Locale,
  storefrontMode: StorefrontMode,
  /** Human-facing only; the property id itself never crosses the wire. */
  propertyLabel: z.string().max(160),
  timeZone: TimeZone,
  currency: CurrencyCode,
  /** PRD GST-007 - address instructions come from the property record. */
  address: AddressInstruction,
  /** PRD CON-008 / OPS-009 - ordering paused for this property, city or globally. */
  servicePaused: z.boolean(),
  /** PRD GST-040 - honest "not yet available here" screen, never a dead end. */
  servable: z.boolean(),
  /**
   * PRD GST-044 - the basket from which delivery is free, VAT included, or null
   * when the delivery grid never makes it so. On the SESSION because it is a
   * fact about the shop and not about a basket: the band under the app bar
   * states it on every screen, and the session is resolved on every load, so a
   * grid changed in Internal reaches the guest on their next one.
   */
  freeDeliveryFrom: MinorUnitsNonNegative.nullable(),
  /**
   * PRD OPS-025 / GST-042 - what the platform is selling right now, which is
   * what shapes this guest's whole journey: the entry step, whether a picker
   * exists at all, and whether the ordering-closed screen is the answer.
   *
   * Read on every scan and never mid-screen: the session is minted per scan, so
   * a switch flipped in Internal takes effect on the next scan and on the next
   * `/g/windows` answer. Checkout re-reads it, so a screen rendered before the
   * flip cannot place an order the platform no longer sells.
   *
   * Senior to neither: `servicePaused` above still wins over both.
   */
  orderingModes: z.object({
    preorder: z.boolean(),
    immediate: z.boolean(),
  }),
  welcomeMessage: z.string().max(2000).nullable(),
  /**
   * NTF-009: checkout offers "order updates on WhatsApp". False while the
   * platform's WhatsApp is switched off, so a guest is never offered a channel
   * that would send them nothing.
   *
   * OPTIONAL on the wire although every arm sends it: this is the one response
   * every guest's first screen validates, and a guest app newer than the worker
   * it reads - the minutes of a deploy, or a `wrangler rollback` of the worker
   * alone - must read an older session as "not offered", not fail to open.
   */
  whatsappUpdatesOffered: z.boolean().optional(),
  /**
   * The morning the storefront opens on.
   *
   * It used to be `nextCutoff` and carry an hour - "Order by 22:00 for
   * tomorrow" - which is gone with the cutoff itself. The DAY is not
   * decoration: the guest app anchors its stored draft on it and drops any
   * morning earlier, so a session naming the wrong one silently threw the
   * draft away.
   */
  nextServiceDay: ServiceDay.nullable(),
});
export type GuestSession = z.infer<typeof GuestSession>;

/**
 * How a guest reaches a human (GST-043): the page behind the support icon in
 * the app bar and behind the small code on the reverse of the printed card.
 *
 * NO SESSION, deliberately, for the reason `GET /b/support` takes no device
 * token: the card's small code opens this page cold, in a browser that may
 * never have scanned an apartment, and a guest who cannot get IN is exactly the
 * guest who needs it.
 *
 * `whatsapp` and `phone` are ADMINISTERED (Internal > Settings), not
 * environment: cards are printed before a number exists, and the day one does
 * it has to reach every card already on a wall without a deploy. Null means
 * "not offered" and the page draws no button for it. `email` is never null -
 * it is the address the card itself prints (WEB-021).
 */
export const GuestSupport = z.object({
  /** The WhatsApp Business number, E.164; the app builds the `wa.me` link from it. */
  whatsapp: PhoneE164.nullable(),
  /** The number a guest can ring. May equal `whatsapp`; the page still offers both actions. */
  phone: PhoneE164.nullable(),
  email: Email,
});
export type GuestSupport = z.infer<typeof GuestSupport>;

export const getSupport = defineEndpoint({
  method: 'GET',
  path: '/g/support',
  mount: 'guest',
  summary: 'How a guest reaches us: WhatsApp, a number to ring, the support address (GST-043)',
  response: GuestSupport,
});

/**
 * A reissued session token, and the two facts that reissuing it can change
 * (GST-006).
 *
 * Picked FROM `GuestSession` rather than restated, so the three fields keep one
 * definition and a caller can merge this object straight onto the session it
 * already holds.
 *
 * It exists because `PATCH /g/session/locale` used to answer with a whole
 * `GuestSession`, which meant rebuilding one: the printed code, the apartment,
 * its owner, the kill switches and a full window offer to state the next
 * cutoff - 17 database round trips, about 3 seconds on the demo instance, to
 * change a language (docs/portal-latency-audit.html). None of it CAN differ
 * from what the caller already holds: the route verifies a token the caller
 * sent, changes one claim in it, and signs it again. The apartment, the mode,
 * the welcome message and the cutoff are the same facts they were a moment ago,
 * and every screen that depends on them refetches on its own schedule.
 *
 * `guestKey` is deliberately absent: the identity is carried over verbatim, so
 * a reissue cannot change it and the caller's copy is still correct.
 */
export const GuestSessionReissue = GuestSession.pick({
  sessionToken: true,
  expiresAt: true,
  locale: true,
});
export type GuestSessionReissue = z.infer<typeof GuestSessionReissue>;

export const setLocale = defineEndpoint({
  method: 'PATCH',
  path: '/g/session/locale',
  mount: 'guest',
  summary: 'Persist the chosen locale on the QR session; drives notifications (GST-006, NTF-004)',
  body: z.object({ locale: Locale }),
  idempotent: false,
  response: GuestSessionReissue,
});

// ---------------------------------------------------------------------------
// Storefront (PRD GST-003, GST-029 to GST-032, CAT-004)
// ---------------------------------------------------------------------------

export const Storefront = z.object({
  mode: StorefrontMode,
  /**
   * Mode A: the allocated bakery's pre-built vitrine, unbranded. Its identity is
   * surfaced nowhere (PRD GST-029, CAT-009).
   * Mode B: populated only once a shop has been selected (PRD GST-030).
   */
  shop: z
    .object({
      id: PublicId,
      displayName: z.string().max(120).nullable(),
      logoUrl: ImageUrl.nullable(),
      heroImageUrl: ImageUrl.nullable(),
      shortDescription: z.string().max(400).nullable(),
      minimumBasket: MinorUnitsNonNegative.nullable(),
    })
    .nullable(),
  items: z.array(CatalogueItem),
  /**
   * The heading over each section of this menu, IN THIS GUEST'S LANGUAGE
   * (CAT-016, NFR-006).
   *
   * The section a product sits in is its `kind`, and a kind's key is whatever
   * the curator first typed - so the storefront used to print `Viennoiseries`
   * over the English menu because a typed label had no second language. The
   * words are authored on the internal Kinds tab now and resolved HERE, beside
   * the item names, through the same locale chain: one answer, computed once,
   * rather than a lookup table shipped to the browser for it to get wrong.
   *
   * EVERY kind on this shelf, in the running order the Kinds tab sets - which
   * is the order `items` above is already in, so the app never has to reconcile
   * two sequences. A kind nobody has written words for carries its own key as
   * its label, exactly as every kind read before the registry existed; there is
   * no second class of kind and no pair the app translates for itself (founder,
   * 2026-08-27).
   */
  sections: z
    .array(
      z.object({
        kind: ProductType,
        label: z.string().min(1).max(40),
        /**
         * Every shipped language (NFR-006); the reader picks with `textIn`, the
         * served field is the session's language and the fallback.
         */
        labelByLocale: ByLocaleText.optional(),
      }),
    )
    .max(60)
    .default([]),
  currency: CurrencyCode,
  serviceDays: z.array(ServiceDay),
});
export type Storefront = z.infer<typeof Storefront>;

export const getStorefront = defineEndpoint({
  method: 'GET',
  path: '/g/storefront',
  mount: 'guest',
  summary: 'Catalogue for the session, per the resolved storefront mode (GST-028 to GST-030)',
  query: z.object({
    serviceDay: ServiceDay.optional(),
    /** Mode B only: the shop the guest selected. */
    shopId: PublicId.optional(),
    /**
     * The window the guest chose before the shop (GST-002, ALC-001, ALC-003).
     *
     * Mode A has no shop-list step at all: the picker is followed straight by
     * this menu, and the bakery behind it is allocated HERE. Passing the chosen
     * window makes it an input to that allocation - the closest bakery that can
     * genuinely serve the slot, which is not always the closest bakery, because
     * the closest one may be shut or open too late. Its identity is still
     * surfaced nowhere (CAT-009, GST-029).
     */
    windowStart: Instant.optional(),
    /**
     * What the guest already holds (issue #1420). Named, the shelf offers only
     * what ONE counter able to serve the morning can hand over together with
     * the basket: an order is baked by one counter, so two products each in
     * stock somewhere but nowhere together are not offered side by side.
     * Omitted, every product some counter can serve is offered.
     */
    itemIds: z.array(PublicId).optional(),
  }),
  response: Storefront,
});

/**
 * MODE B2's ONE SHOP, WITH NOBODY NAMED (founder decision, 2026-09-20).
 *
 * The first B2 screen is two buttons - "Order now" and "Schedule your
 * delivery" - and this is everything either of them needs to say: whether the
 * shop is open right now, today's hours and delivery line, the weekly opening
 * days, the first morning it can serve, how far it is, and the minimum basket.
 *
 * It answers for the shop the SESSION is bound to and takes no parameter: in
 * B2 the guest chooses nothing, so there is nothing to name. A mode A or mode B
 * session gets `409 ordering_closed` too - there is no bound shop on either,
 * and neither app has a screen for this.
 *
 * `GET /g/shops` is the mode B chooser and stays that; a B2 session is refused
 * there outright, because a list of named houses is precisely what B2 does not
 * show.
 */
export const getShopFacts = defineEndpoint({
  method: 'GET',
  path: '/g/shop-facts',
  mount: 'guest',
  summary: "Mode B2: the bound shop's days, hours, reach and minimum - no identity (GST-030)",
  response: z.object({
    /**
     * The morning `facts.pickupHours` and `facts.deliverySpans` describe, so
     * the screen never has to work out which day it is reading about.
     */
    serviceDay: ServiceDay,
    facts: ShopFacts,
  }),
});

export const listShops = defineEndpoint({
  method: 'GET',
  path: '/g/shops',
  mount: 'guest',
  summary:
    'Mode B: named shops that can genuinely serve this address and morning (GST-030, GST-031)',
  query: z.object({ serviceDay: ServiceDay.optional() }),
  response: z.object({
    serviceDay: ServiceDay,
    shops: z.array(ShopCard),
  }),
});

// ---------------------------------------------------------------------------
// Window picker (PRD GST-002, GST-017, RTE-008, RTE-028)
// ---------------------------------------------------------------------------

export const OfferableWindow = z.object({
  window: DeliveryWindow,
  /** False windows are shown greyed out, never selectable (PRD GST-002). */
  selectable: z.boolean(),
  /** i18n key explaining why it is not selectable: capacity, cutoff, bakery closed. */
  unavailableReasonKey: z.string().nullable(),
  /**
   * PRD RTE-034 - this is TODAY's one offer: the guest reads the word
   * "Soonest", and no time is rendered against it.
   *
   * The times on this entry are provisional and are deliberately not shown. An
   * immediate arrival is a courier's estimate for a specific pickup, and at
   * picker time there is no basket, so no pickup and nothing to ask a courier
   * about; the real range arrives on the QUOTE (`immediateArrival`) once the
   * basket is settled. Showing the provisional window here is what made a
   * 19:26 scan promise 20:15-20:45 out of a thirty-minute fallback allowance
   * nobody had measured.
   *
   * Always false on a pre-order morning, where every window is a real chosen
   * slot and equally far away.
   */
  soonest: z.boolean(),
});
export type OfferableWindow = z.infer<typeof OfferableWindow>;

export const listWindows = defineEndpoint({
  method: 'GET',
  path: '/g/windows',
  mount: 'guest',
  summary: 'Service days and the offerable 30-minute windows per day (GST-002, RTE-028)',
  query: z.object({
    /**
     * The FIRST morning of the horizon, not the only one: the answer runs
     * forward from here. Omitted, it starts at the apartment's tomorrow.
     */
    serviceDay: ServiceDay.optional(),
    shopId: PublicId.optional(),
    /** Basket contents matter: per-product caps are part of capacity (RTE-008). */
    itemIds: z.array(PublicId).optional(),
    /**
     * How many mornings to answer with, server-clamped to the platform
     * horizon (14 - decision D7, multi-day plan). Omitted means the full
     * horizon: the day strip IS the stay selector (D11), so the default is
     * everything a guest may select.
     */
    days: z.coerce.number().int().min(1).max(60).optional(),
  }),
  response: z.object({
    /**
     * Every orderable morning of the horizon, ascending - each with its own
     * cutoff and its own windows (GST-002).
     *
     * A morning nothing can serve is RETURNED, with an empty window list
     * and no windows, so the picker greys it out and says why. Omitting it makes
     * the selector look broken to a guest who is simply too late for that day.
     *
     * With neither `shopId` nor `itemIds`, a morning's windows are the UNION
     * across every bakery that could serve the address that day: a window is
     * offered when SOMEONE can serve it, which is what makes choosing the time
     * before the shop safe. Narrow the answer by naming a shop or a basket and
     * it is exactly that bakery's own offer, as precise as it has always been.
     */
    serviceDays: z.array(
      z.object({
        serviceDay: ServiceDay,
        /**
         * PRD RTE-034 - which product this morning is. `TODAY` is the immediate
         * day: it starts at the soonest deliverable window rather than at the
         * morning's first, and only windows still ahead are listed. Every other
         * day is `PREORDER` and behaves as it always has.
         */
        kind: z.enum(['TODAY', 'PREORDER']),
        windows: z.array(OfferableWindow),
      }),
    ),
    timeZone: TimeZone,
    /**
     * The platform's own now, as the clock that produced these cutoffs read it.
     *
     * The day strip labels one card "Today" and the next "Tomorrow", and until
     * this field existed it decided which by asking the GUEST'S BROWSER. That is
     * nobody's clock: outside production the platform runs on the rehearsal
     * clock (TST-003) and can be hours ahead of wall time, so a strip jumped ten
     * hours over midnight greyed the morning it was calling "Today". It carries
     * on THIS response rather than on an endpoint of its own precisely because
     * these two facts have to agree - the now that greyed a cutoff and the now
     * that names the day are then the same reading, by construction. The browser
     * derives an offset from it and ticks (`usePlatformNow`, `@croissants/ui`),
     * so a screen open for an hour is still on the platform's day.
     */
    serverTime: Instant,
  }),
});

/**
 * What the first screen after a scan reads, answered INSIDE the scan.
 *
 * Every field is exactly the answer its own endpoint gives a fresh session
 * asking with no parameter at all - `GET /g/storefront`, `/g/shop-facts`,
 * `/g/windows` and `/g/shops` - computed with the token this scan minted. The
 * guest app files each one under the key it would have asked with, so its
 * first screen reads a cache instead of opening a second wave of requests,
 * each of which paid the session gate again. A field is absent when the mode
 * never asks for it first, or when the endpoint would have refused: the app
 * then asks as it always has, and is told what it always was.
 */
export const GuestFirstScreen = z.object({
  storefront: getStorefront.response.optional(),
  shopFacts: getShopFacts.response.optional(),
  windows: listWindows.response.optional(),
  shops: listShops.response.optional(),
});
export type GuestFirstScreen = z.infer<typeof GuestFirstScreen>;

export const resolveSession = defineEndpoint({
  method: 'POST',
  path: '/g/session/resolve',
  mount: 'guest',
  summary: 'Resolve a signed QR token to one active apartment (GST-001, GST-028, GST-040)',
  body: z.object({
    /** The signed token from the QR payload. */
    qrToken: z.string().min(16).max(2048),
    /** Browser language; the storefront opens in it when supported (GST-006). */
    preferredLocale: Locale.optional(),
    /**
     * PRD GST-034 / NFR-008 - the guest key this browser kept from an earlier
     * scan. The printed code identifies the apartment; this identifies the
     * guest, and only the pair reaches an order. Absent on a first scan and
     * after the guest clears their browser, which is exactly when the previous
     * guest's orders must NOT appear.
     */
    guestKey: z.string().min(8).max(256).optional(),
  }),
  idempotent: false,
  /* The session, and the first screen's reads beside it (`GuestFirstScreen`). */
  response: GuestSession.extend({ firstScreen: GuestFirstScreen.optional() }),
});

// ---------------------------------------------------------------------------
// Basket and quote (PRD GST-022, GST-026, CAT-014)
// ---------------------------------------------------------------------------

/**
 * One answer to one bullet of a formule's menu card (CAT-018).
 *
 * Keyed on the bullet's RANK rather than on its position in this array, so a
 * basket restored from storage still says which question it answered even if
 * the card has since been re-ordered - and so the server can check that every
 * bullet needing an answer got exactly one.
 */
export const BasketChoice = z.object({
  componentRank: z.int().min(0),
  itemId: PublicId,
});
export type BasketChoice = z.infer<typeof BasketChoice>;

/**
 * A line is a PRODUCT AND ITS ANSWERS, not a product (founder, 2026-08-28).
 *
 * Two Montmartre formules, one with orange juice and one with pear, are two
 * lines carrying the same `itemId` - so nothing here may assume the ids are
 * unique, and the server prices, names and bags them independently. Merging
 * them on the id is what would lose the second guest's choice.
 *
 * `choices` is absent on every plain product and on every client older than
 * this field; a formule whose card has an open bullet is refused at checkout
 * rather than bagged by whoever picks it up.
 */
export const BasketLine = z.object({
  itemId: PublicId,
  quantity: z.int().positive().max(99),
  choices: z.array(BasketChoice).max(20).optional(),
});
export type BasketLine = z.infer<typeof BasketLine>;

export const Basket = z.object({
  shopId: PublicId.nullable(),
  lines: z.array(BasketLine).min(1),
  serviceDay: ServiceDay,
  windowStart: Instant,
  /**
   * PRD RTE-034: this is today's ASAP row - "as soon as we can" - and not a
   * time the guest named.
   *
   * Today sells both, and `windowStart` cannot tell them apart. The ASAP row
   * carries a PROVISIONAL window that moves with the clock, and its start
   * lands on the offer cadence often enough to matter: at 08:00 with the
   * fallback travel allowance it is exactly 08:45, which is also a real
   * afternoon-style slot on the same list. A minute later the ASAP row has
   * moved and the named slot has not, so the same request would read as the
   * slot - and be promised for 08:45 with the counter told to bag it now.
   *
   * So the client says which row it tapped. Absent means a named slot, which
   * is what every pre-order morning sends and what a client older than this
   * field meant.
   */
  soonest: z.boolean().optional(),
});
export type Basket = z.infer<typeof Basket>;

/** One morning of a repeat stay, priced (PRD GST-010, GST-021). */
export const QuotedMorning = z.object({
  serviceDay: ServiceDay,
  window: DeliveryWindow,
  /**
   * The DISPLAYED per-day figure (D8): the stay total divided by the days,
   * rounded once by the stay tariff in `packages/pricing` - the same figure
   * on every servable row, never that morning's internal total. Null when no
   * bakery can serve that morning - shown, never silently dropped.
   */
  total: MinorUnitsNonNegative.nullable(),
  /**
   * Why the morning cannot be served, when it cannot (multi-day plan
   * section 2.3): the same closed set checkout reports, so the review can
   * distinguish "closed that day" from "that hour is full" - the second
   * invites picking another window; the first does not. Null when priced.
   */
  reasonKey: StaySkipReason.nullable(),
});
export type QuotedMorning = z.infer<typeof QuotedMorning>;

/**
 * The whole stay, when the guest asked for one (PRD GST-010).
 *
 * Present because every morning of a stay is charged at checkout, so the review
 * screen has to state the figure the guest is committing to rather than the
 * first morning's share of it.
 */
export const QuotedStay = z.object({
  mornings: z.array(QuotedMorning).min(1).max(60),
  /**
   * What will actually be charged (D8): days x the rounded per-day average
   * of the servable mornings' real totals - the stay tariff's figure, with
   * the odd cents absorbed in the internal service-fee line, never a UI
   * division and never a displayed summed fee.
   */
  total: MinorUnitsNonNegative,
  unavailable: z.array(ServiceDay),
});
export type QuotedStay = z.infer<typeof QuotedStay>;

export const quote = defineEndpoint({
  method: 'POST',
  path: '/g/quote',
  mount: 'guest',
  summary: 'Price a basket: merchandise, delivery, service, tax, promotion, all-in total (GST-022)',
  body: Basket.extend({
    /**
     * The further mornings of a repeat stay (GST-010), excluding the basket's
     * own `serviceDay`. Given these, the response carries `stay`.
     */
    repeatServiceDays: z.array(ServiceDay).max(59).optional(),
  }),
  idempotent: false,
  response: z.object({
    /**
     * The itemised total (GST-022). For a stay (`repeatServiceDays` sent) it
     * is DAY 1 AS DISPLAYED (D8): real merchandise, averaged delivery and
     * service rows summing to the per-day figure every stay row reads - the
     * internal per-morning figures never reach the wire.
     */
    price: PriceBreakdown,
    /** PRD CAT-014 - restated at checkout and enforced before payment. */
    minimumBasket: MinorUnitsNonNegative.nullable(),
    minimumBasketMet: z.boolean(),
    window: DeliveryWindow,
    /**
     * PRD GST-002 - is the window the guest chose STILL offerable for this
     * basket, from the bakery this quote is for?
     *
     * A basket's preparation time grows as items are added and capacity is taken
     * by other orders, so a slot chosen at the picker can stop fitting before
     * the guest reaches the Total. Checkout refuses it (409 `window_unavailable`)
     * and it must not be the first the guest hears of it: the review screen is
     * where "that slot no longer fits" belongs, beside the alternatives, while
     * the basket is still editable.
     */
    windowStillOfferable: z.boolean(),
    /**
     * The nearest windows that DO still fit, in start order - empty when
     * `windowStillOfferable` is true, and empty when nothing else fits either.
     * From the same routing answer the check above used, so the screen cannot
     * offer a replacement the checkout would refuse in turn.
     */
    alternativeWindows: z.array(DeliveryWindow),
    /** Null unless `repeatServiceDays` was sent (GST-010). */
    stay: QuotedStay.nullable(),
    /**
     * PRD RTE-034 - WHEN an order placed now arrives, for an order for today.
     *
     * This is the first moment the platform can answer that honestly, and it is
     * why the picker deliberately says "Soonest" with no time against it: an
     * immediate arrival is a COURIER'S estimate for a specific pickup, and until
     * the basket is settled there is no pickup to ask about. Quoted here from
     * the counter's own measured pace, so the range is the provider's answer
     * rather than a margin the platform added to a guess.
     *
     * Null on a pre-order, which has no such answer to give: its arrival is the
     * window the guest chose, which is `window` above.
     */
    immediateArrival: DeliveryWindow.nullable(),
    /**
     * GST-044: what the delivery grid offers THIS basket, stated beside the
     * price and never inside it - `price`'s rows add up to its total and
     * neither figure here is one of them.
     *
     * `incentive` is the next step of the grid: how much more merchandise
     * reaches it, and what the DELIVERY fees come down by when it does - the
     * delivery row, plus the small-order fee when the basket pays one now and
     * would not at the step; never the service fee (founder, 2026-09-21). Null
     * when there is no next step and when reaching it would not lower them. On
     * a stay it is ONE order's - the first morning's, the basket being the same
     * every morning - never the stay's fees summed. The review no longer
     * DRAWS it - the line under the address does, off `deliveryOffer` below -
     * and it stays here because it is the same answer, priced on the same
     * courier question. `freeDeliveryFrom` is the basket from which delivery is free,
     * whatever this basket holds; null when the grid never makes it so. All
     * VAT included, computed by the pricing engine - a screen formats them and
     * derives nothing.
     */
    deliveryOffer: z.object({
      incentive: z
        .object({ addToBasket: MinorUnitsNonNegative, saving: MinorUnitsNonNegative })
        .nullable(),
      freeDeliveryFrom: MinorUnitsNonNegative.nullable(),
      /**
       * What each fee row would read with no offer on it, VAT included, or null
       * when nothing was taken off that row. The review strikes it beside the
       * figure charged - on a stay too, on that row's own basis: the per-day
       * average of each morning's full price beside the averaged row. `serviceFee` is always null today - nothing discounts
       * the service fee yet - and is named so the row is ready when something does.
       */
      fullPrice: z.object({
        deliveryFee: MinorUnitsNonNegative.nullable(),
        serviceFee: MinorUnitsNonNegative.nullable(),
      }),
    }),
  }),
});

/**
 * GST-044: the saving the line under the address states for the basket the
 * guest is holding NOW - "Add 12.40 to save 5.65 on your delivery fees. Free
 * delivery above 70.00." (founder, 2026-09-21).
 *
 * The QUOTE's answer, asked without a chosen time: the same bakery, the same
 * courier question and the same pricing arithmetic, so on the review the two
 * cannot disagree and on the shelf the review reuses this courier price. It is
 * `quote`'s body with the window OPTIONAL - `serviceDay` is the draft's morning
 * or the session's `nextServiceDay` - and it never refuses: a basket nobody can
 * serve, a paused service or a stale session answer `incentive: null`, and the
 * screens that exist for those say so. A stay is asked about its FIRST morning
 * alone: every morning is its own order with the same basket, so the saving is
 * stated per order and the further mornings are not part of the question.
 */
export const deliveryOffer = defineEndpoint({
  method: 'POST',
  path: '/g/delivery-offer',
  mount: 'guest',
  summary: "What reaching the delivery grid's next step saves THIS basket (GST-044)",
  body: Basket.extend({
    windowStart: Instant.optional(),
  }),
  idempotent: false,
  response: z.object({
    /** As on the quote: VAT included, computed by the pricing engine. */
    incentive: z
      .object({ addToBasket: MinorUnitsNonNegative, saving: MinorUnitsNonNegative })
      .nullable(),
    freeDeliveryFrom: MinorUnitsNonNegative.nullable(),
  }),
});

// ---------------------------------------------------------------------------
// Checkout (PRD GST-005, GST-006, GST-019 to GST-024, PAY-002)
// ---------------------------------------------------------------------------

export { GUEST_NOTE_MAX };

export const GuestContact = z.object({
  /**
   * The whole name, in one field.
   *
   * It was two, and the second half was collected, made mandatory and thrown
   * away: no entity, column, provider call or screen ever read the halves
   * apart, the courier is handed this one, and Uber Direct's manifest takes a
   * single string. The payment sheet has asked one question since PRD 10.6;
   * this is the wire catching up (GST-006).
   */
  firstName: z.string().min(1).max(80),
  /** Required, used only for delivery contact (PRD GST-006). */
  phone: PhoneE164,
  email: Email.optional(),
  locale: Locale,
});
export type GuestContact = z.infer<typeof GuestContact>;

export const CheckoutRequest = z.object({
  basket: Basket,
  contact: GuestContact,
  substitutionChoice: SubstitutionChoice,
  /**
   * PRD CAT-011 - the guest declares an allergen concern on this order.
   *
   * The counter must never swap an item on a flagged order: a stock-out is
   * refunded off the ticket instead, whatever `substitutionChoice` says, and
   * `resolveStockOut`/`listReplacementOptions` in `/b` enforce exactly that.
   * The flag has always been a `checkout` use-case input and an `orders`
   * column, and until this field existed NOTHING on the wire could set one -
   * so the ban was unreachable outside a seeded row (finding F-19). Optional
   * rather than defaulted, so a client that never mentions allergies sends the
   * same body it always did and the column keeps its own `false`.
   */
  allergenFlag: z.boolean().optional(),
  /**
   * PRD GST-163 - the guest's note to the house, typed on the basket. It
   * reaches the counter's screen and slip (BAK-266) and never the courier.
   * Absent is no note.
   */
  guestNote: z.string().max(GUEST_NOTE_MAX).optional(),
  /** PRD GST-023 - order-scoped override, never a mutation of the property record. */
  deliveryOverride: DeliveryOverride.nullable(),
  /** PRD GST-010 - create a repeat schedule from checkout. */
  repeat: z
    .object({
      serviceDays: z.array(ServiceDay).min(1).max(60),
      stayEndDate: ServiceDay,
    })
    .nullable(),
  /** PRD WEB-007 - the version of the terms the guest accepted. */
  acceptedTermsVersion: z.string().max(40),
  /**
   * Payment-method reference from the payment element (PAY-002, PAY-012).
   * Optional: in dev/rehearsal the server substitutes the gateway's test method.
   */
  paymentMethodRef: z.string().max(200).optional(),
  /**
   * NTF-009: the guest ticked "order updates on WhatsApp" - never pre-ticked,
   * offered only while `GuestSession.whatsappUpdatesOffered`. The updates go to
   * `contact.phone`. Absent is no.
   */
  whatsappUpdates: z.boolean().optional(),
});
export type CheckoutRequest = z.infer<typeof CheckoutRequest>;

export const checkout = defineEndpoint({
  method: 'POST',
  path: '/g/checkout',
  mount: 'guest',
  summary: 'Authorize payment and place the order; idempotent (GST-005, PAY-002, PAY-006)',
  body: CheckoutRequest,
  idempotent: true,
  response: z.object({
    order: OrderSummary,
    /**
     * Stripe client secret for the payment element. Card data never touches the
     * platform (PRD GST-005).
     */
    paymentClientSecret: z.string().max(512),
    publishableKeyHint: z.string().max(20).nullable(),
    scheduleId: Uuid.nullable(),
    /**
     * The mornings of a repeat stay (GST-010): every morning the request
     * named, with the public id of the order placed for it. Null on a checkout
     * without `repeat`. A stay is placed whole or refused whole (409
     * `stay_morning_unavailable`, GST-098), so every morning listed here has
     * its order.
     */
    stay: z
      .object({
        mornings: z
          .array(
            z.object({
              serviceDay: ServiceDay,
              orderPublicId: PublicId.nullable(),
            }),
          )
          .min(1)
          .max(60),
        /**
         * The stay total actually committed (D8): days x the rounded per-day
         * average over the mornings this checkout placed - what the charges
         * will sum to. Nullable only for idempotent replays of checkouts
         * stored before the tariff existed.
         */
        total: MinorUnitsNonNegative.nullable(),
      })
      .nullable(),
  }),
});

// ---------------------------------------------------------------------------
// Orders (PRD GST-008, GST-009, GST-034, GST-037 to GST-039)
// ---------------------------------------------------------------------------

export const listOrders = defineEndpoint({
  method: 'GET',
  path: '/g/orders',
  mount: 'guest',
  summary: 'Orders screen: active orders on top, past orders below (GST-034)',
  query: PageRequest,
  response: z.object({
    active: z.array(OrderSummary),
    past: pageOf(OrderSummary),
    /**
     * The platform's clock, for the same reason the window strip carries one
     * (see `serverTime` above, and `platform-clock.ts` in `@croissants/ui`).
     *
     * An active row is badged by how far off its morning is - "Tomorrow", "In
     * 3 days" - and that is a question about the platform's day, not the
     * device's. Outside production the platform runs on the rehearsal clock
     * and can be hours ahead of wall time; a phone is also simply allowed to
     * be wrong. Asking the browser would badge a morning "Tomorrow" that the
     * platform had already started baking.
     */
    serverTime: Instant,
  }),
});

export const getOrder = defineEndpoint({
  method: 'GET',
  path: '/g/orders/:orderPublicId',
  mount: 'guest',
  summary: 'No-login order detail with the live stage timeline (GST-008, GST-036)',
  params: z.object({ orderPublicId: PublicId }),
  response: OrderDetail,
});

export const cancelOrder = defineEndpoint({
  method: 'POST',
  path: '/g/orders/:orderPublicId/cancel',
  mount: 'guest',
  summary:
    'Self-serve cancellation, open until acceptance; the refund is an operator-approved case (GST-009)',
  params: z.object({ orderPublicId: PublicId }),
  body: z.object({ reason: z.string().max(500).optional() }),
  idempotent: true,
  response: z.object({
    order: OrderSummary,
    /**
     * What went back at the cancellation itself: always 0 since the thin
     * refund cases (29 September 2026) - the refund is one case an operator
     * approves after the order is final. Kept for the wire's shape.
     */
    refundAmount: MinorUnitsNonNegative,
  }),
});

/**
 * The browser telling us the hold it just took is real (GST-005, PAY-002).
 *
 * A SHORTCUT, not the authority. The gateway's own webhook confirms every
 * payment whatever the browser does - closes the tab, loses signal, dies - and
 * this endpoint only exists so the guest is not shown a "waiting for payment"
 * screen for the second or two that round trip takes. It is idempotent against
 * the webhook by construction: both run the same confirmation, and the second
 * one finds an order that has already moved.
 *
 * It carries no payment facts on purpose. Anything the browser asserted about
 * an amount or a method would have to be verified against the gateway anyway,
 * so it asserts nothing and the server asks the gateway.
 */
export const confirmPayment = defineEndpoint({
  method: 'POST',
  path: '/g/orders/:orderPublicId/payment-confirmed',
  mount: 'guest',
  summary: 'Guest-side notice that the payment hold succeeded; the webhook is authoritative',
  params: z.object({ orderPublicId: PublicId }),
  idempotent: true,
  response: z.object({ order: OrderSummary }),
});

export const tipCourier = defineEndpoint({
  method: 'POST',
  path: '/g/orders/:orderPublicId/tip',
  mount: 'guest',
  summary:
    'Post-delivery courier tip, charged off-session and forwarded verbatim (GST-037, PAY-024)',
  params: z.object({ orderPublicId: PublicId }),
  body: z.object({ amount: MinorUnitsNonNegative.min(1) }),
  idempotent: true,
  response: z.object({
    amount: MinorUnitsNonNegative,
    chargedAt: Instant,
  }),
});

export const rateOrder = defineEndpoint({
  method: 'POST',
  path: '/g/orders/:orderPublicId/ratings',
  mount: 'guest',
  summary: 'Two distinct skippable ratings: delivery experience and products (GST-038)',
  params: z.object({ orderPublicId: PublicId }),
  body: z.object({
    ratings: z
      .array(
        z.object({
          subject: RatingSubject,
          stars: z.int().min(1).max(5),
          comment: z.string().max(2000).optional(),
        }),
      )
      .min(1)
      .max(2),
  }),
  idempotent: false,
  response: Ok,
});

/**
 * The photograph a guest just took, on its way to the object store (GST-039).
 *
 * It exists because `reportIssue.photoUrl` is an `ImageUrl`, and an `ImageUrl`
 * refuses a `data:` URI in both directions - every image on this platform is an
 * object in R2 and every field holds a reference to one. The issue form used to
 * put the encoded photograph straight into that field, so the report was
 * refused at the boundary the moment a guest attached one, and the screen said
 * only that something had gone wrong. The photo is stored first now and the
 * report carries the address it was given back.
 *
 * The order in the path is what scopes it: a session may only attach a
 * photograph to an order it can already read, so this cannot become an open
 * upload endpoint for anyone holding a guest session.
 */
export const uploadIssuePhoto = defineEndpoint({
  method: 'POST',
  path: '/g/orders/:orderPublicId/issue-photo',
  mount: 'guest',
  summary: 'Store a photo for an issue report and return its address (GST-039)',
  params: z.object({ orderPublicId: PublicId }),
  body: z.object({
    /** Base64, no data-URI prefix. The ceiling is the object store's own. */
    data: z.string().min(16).max(700_000),
    /** What the browser re-encodes to (`guest-web/src/lib/photo.ts`). */
    contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  }),
  idempotent: false,
  response: z.object({ url: ImageUrl }),
});

export const reportIssue = defineEndpoint({
  method: 'POST',
  path: '/g/orders/:orderPublicId/issues',
  mount: 'guest',
  summary: 'In-page issue report with per-line selection, optional photo, free text (GST-039)',
  params: z.object({ orderPublicId: PublicId }),
  body: z.object({
    lineIds: z.array(Uuid).max(50),
    photoUrl: ImageUrl.optional(),
    text: z.string().min(1).max(4000),
    /**
     * Where support answers, and REQUIRED (SUP-006, founder decision
     * 2026-08-25).
     *
     * The platform already has the guest's mobile, and it is not enough on its
     * own: guest SMS is one-way by construction (SUP-009 - notifications go out
     * from an alphanumeric sender, so there is no thread to reply into), the
     * guest may have left Paris by the time anybody reads the report, and some
     * answers do not fit in a text.
     *
     * Asked for here rather than taken from the order, because an order can be
     * placed with no email at all (GST-004) and the address a guest can be
     * reached at now is not necessarily the one they typed that morning. It is
     * stored on the report and it is what the support mail is sent from the
     * platform's side to, so a reply lands where the guest expects it.
     */
    contactEmail: Email,
  }),
  idempotent: true,
  response: IssueReport,
});

export const reorder = defineEndpoint({
  method: 'POST',
  path: '/g/orders/:orderPublicId/reorder',
  mount: 'guest',
  summary: 'Rebuild a past basket for a new service day (GST-010, GST-034)',
  params: z.object({ orderPublicId: PublicId }),
  idempotent: false,
  response: z.object({ basket: Basket.partial({ windowStart: true, serviceDay: true }) }),
});

/*
 * GDPR (GST-018) has NO endpoint, deliberately (founder decision 2026-08-25).
 *
 * There were two: `POST /g/data-requests` texted a six-digit code to any E.164
 * number a caller named, and `/g/data-requests/verify` exchanged that code for
 * an export or an erasure. Both were unauthenticated of necessity - a guest has
 * no login, and NFR-008 forbids the answer varying with whether the number is
 * known, so the gate could be neither a session nor a lookup - and the first
 * was therefore the only unauthenticated write on the platform that sent a
 * message to an address the CALLER chose.
 *
 * A bot check and a per-number limit were fitted first and were not judged
 * enough to keep a public SMS sender alive for a feature nothing called: the
 * guest-facing entry point had already been removed on 2026-08-19, and a
 * per-number limit bounds the harassment of one person while bounding nothing
 * about the bill. Security exposure won.
 *
 * Access and erasure are processed by hand from an email now, with identity
 * established by what the requester can state about their order - its date, its
 * exact amount, the apartment - which is enough to find the record. That is a
 * weaker proof than possession of the phone and it is a deliberate trade; the
 * options for restoring self-serve are in GitHub issue #554, at P3.
 *
 * Do not re-add either endpoint without reading that issue first.
 */

/**
 * One of the guest's invoices, as the PDF stored when it was issued
 * (docs/finance/invoicing.html#delivery and #storage): the platform's fee invoice or
 * the baker's product invoice, and the credit notes that correct them.
 *
 * Reached from a signed link, the receipt's scheme: `sig` is an HMAC over
 * `document:<documentId>` with the session secret, so the link opens from a
 * plain browser navigation with no session header, names one document and
 * nothing else, and never incorporates the host (OPS-020). The document must
 * belong to the order in the path.
 */
export const getOrderDocument = defineEndpoint({
  method: 'GET',
  path: '/g/orders/:orderPublicId/documents/:documentId',
  mount: 'guest',
  summary: "One of the guest's invoices, as its stored PDF (PAY-022, invoicing section 8)",
  params: z.object({ orderPublicId: PublicId, documentId: Uuid }),
  query: z.object({ sig: z.string().min(1).max(128) }),
  /* A PDF, so there is no shape the typed client could parse. */
  response: z.never(),
});

/**
 * "Request an invoice" (docs/finance/invoicing.html#delivery): issues the
 * baker's product invoice in the house's name if the order has none, records
 * the request, and answers with the guest's documents - the same ones every
 * press after the first. Emails both once, when the order carries an address.
 * 409 `error.order_not_closed` before the order closes.
 */
export const requestInvoices = defineEndpoint({
  method: 'POST',
  path: '/g/orders/:orderPublicId/invoices',
  mount: 'guest',
  summary: "The guest's two invoices, on request (invoicing.html#delivery)",
  params: z.object({ orderPublicId: PublicId }),
  body: z.object({}),
  // Idempotent by construction: a second press finds the first's documents.
  idempotent: false,
  response: z.object({
    documents: z.array(DocumentSummary),
    productsInvoice: ProductsInvoiceStanding,
  }),
});

export const guestEndpoints = {
  getSupport,
  resolveSession,
  setLocale,
  getStorefront,
  getShopFacts,
  listShops,
  listWindows,
  quote,
  deliveryOffer,
  checkout,
  listOrders,
  getOrder,
  cancelOrder,
  confirmPayment,
  tipCourier,
  rateOrder,
  uploadIssuePhoto,
  reportIssue,
  reorder,
  getOrderDocument,
  requestInvoices,
} as const;

/** Header the guest apps send with the session token issued by `resolveSession`. */
export const GUEST_SESSION_HEADER = 'x-guest-session';
/** Fresh bearer credentials returned after a valid authenticated guest request. */
export const GUEST_SESSION_RENEWED_HEADER = 'x-guest-session-renewed';
export const GUEST_SESSION_EXPIRES_HEADER = 'x-guest-session-expires';

export { IdempotencyKey };
